import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Briefcase, MapPin, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO, isValid } from 'date-fns';

interface AvailabilityCardProps {
  availableFrom?: string | null;
  noticePeriod?: string | null;
  currentPosition?: string | null;
  preferredLocations?: string[] | null;
  willingToRelocate?: boolean;
  className?: string;
}

const formatNoticePeriod = (notice: string): string => {
  const labels: Record<string, string> = {
    'immediate': 'Immediately Available',
    '1_week': '1 Week Notice',
    '2_weeks': '2 Weeks Notice',
    '1_month': '1 Month Notice',
    '2_months': '2 Months Notice',
    '3_months': '3 Months Notice',
  };
  return labels[notice] || notice.replace(/_/g, ' ');
};

export const AvailabilityCard: React.FC<AvailabilityCardProps> = ({
  availableFrom,
  noticePeriod,
  currentPosition,
  preferredLocations,
  willingToRelocate,
  className
}) => {
  const hasContent = availableFrom || noticePeriod || currentPosition || (preferredLocations && preferredLocations.length > 0);
  
  if (!hasContent) return null;

  const formatDate = (dateStr: string): string => {
    try {
      const date = parseISO(dateStr);
      if (isValid(date)) {
        return format(date, 'MMM d, yyyy');
      }
    } catch (e) {}
    return dateStr;
  };

  return (
    <Card className={cn("border-sky-200/60 dark:border-sky-700/40 bg-gradient-to-br from-sky-50/50 to-blue-50/30 dark:from-sky-900/20 dark:to-blue-900/10", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-sky-700 dark:text-sky-400">
          <Calendar className="w-4 h-4" />
          Availability
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Current Position */}
        {currentPosition && (
          <div className="flex items-start gap-3">
            <div className="p-1.5 rounded-full bg-sky-100 dark:bg-sky-900/40">
              <Briefcase className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Current Position</p>
              <p className="text-sm font-medium">{currentPosition}</p>
            </div>
          </div>
        )}

        {/* Available From */}
        {availableFrom && (
          <div className="flex items-start gap-3">
            <div className="p-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40">
              <Calendar className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Available From</p>
              <p className="text-sm font-medium">{formatDate(availableFrom)}</p>
            </div>
          </div>
        )}

        {/* Notice Period */}
        {noticePeriod && (
          <div className="flex items-start gap-3">
            <div className="p-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40">
              <Clock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Notice Period</p>
              <p className="text-sm font-medium">{formatNoticePeriod(noticePeriod)}</p>
            </div>
          </div>
        )}

        {/* Preferred Locations */}
        {preferredLocations && preferredLocations.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Preferred Locations</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {preferredLocations.map((loc, idx) => (
                <Badge key={idx} variant="secondary" className="text-[10px]">
                  {loc}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Willing to Relocate */}
        {willingToRelocate !== undefined && (
          <div className="pt-2 border-t border-sky-200/50 dark:border-sky-700/30">
            <Badge 
              variant="outline" 
              className={cn(
                "text-xs",
                willingToRelocate 
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400" 
                  : "bg-slate-50 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700"
              )}
            >
              {willingToRelocate ? '✓ Willing to Relocate' : 'Prefers Current Location'}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailabilityCard;
